import * as XLSX from 'xlsx';
import {
    getAsRepairExcelFieldMap,
} from './asRepairExcelImport';
import {
    AS_REPAIR_MANAGEMENT_SCOPE,
    formatAsRepairAutoDisplay,
    formatAsRepairQuantityDisplay,
    getAsRepairFieldDisplayValue,
    normalizeAsRepairFormConfig,
    parseQuantityValue,
    shouldAlwaysShowAsRepairDetailField,
} from './asRepairFormConfig';

/** A/S 수리 관리 — 수리내역 리스트 다운로드 기본 열 (A열부터 연속) */
const REPAIR_HISTORY_FIXED_COLUMNS = [
    { role: 'boxNumber', header: '박스번호' },
    { role: 'modelName', header: '모델명' },
    { role: 'siteName', header: '현장명' },
    { role: 'totalQty', header: '전체수량' },
    { role: 'shipDate', header: '출고일자' },
];

/** A/S 개인 관리 — 수리완료 리스트 다운로드 기본 열 (A열부터 연속) */
const PERSONAL_REPAIR_COMPLETE_FIXED_COLUMNS = [
    { role: 'boxNumber', header: '박스번호' },
    { role: 'modelName', header: '모델명' },
    { role: 'itemName', header: '품목명' },
    { role: 'totalQty', header: '전체수량' },
    { role: 'siteName', header: '현장명' },
    { role: 'memo', header: '비고사항' },
];

/** 리스트 세부사항과 동일한 필드 순서 */
export const getAsRepairDetailExportFields = (config, listSummary, excludeKeys = new Set()) => {
    const fields = normalizeAsRepairFormConfig(config).fields;
    const summaryKeys = listSummary?.summaryKeys || new Set();
    return fields.filter((field) => {
        if (!field?.key || excludeKeys.has(field.key)) {
            return false;
        }
        return (
            !summaryKeys.has(field.key)
            || field.inputType === 'textarea'
            || field.inputType === 'text'
            || field.inputType === 'quantity'
            || shouldAlwaysShowAsRepairDetailField(field)
        );
    });
};

const isZeroExportValue = (raw) => {
    if (raw == null || raw === '') {
        return true;
    }
    if (raw === 0) {
        return true;
    }
    const text = String(raw).trim();
    if (text === '' || text === '-' || text === '0' || text === '0.0') {
        return true;
    }
    if (/^-?\d+(\.0+)?$/.test(text) && Number(text) === 0) {
        return true;
    }
    return false;
};

const cellValueForExport = (field, row, config, role = null) => {
    if (!field) {
        return '';
    }
    const raw = getAsRepairFieldDisplayValue(field, row, config);
    if (isZeroExportValue(raw)) {
        return '';
    }
    if (field.inputType === 'auto') {
        const text = formatAsRepairAutoDisplay(raw);
        if (isZeroExportValue(text)) {
            return '';
        }
        return text;
    }
    if (field.inputType === 'quantity' || role === 'totalQty') {
        const text = formatAsRepairQuantityDisplay(raw);
        return text === '' ? '' : parseQuantityValue(raw);
    }
    if (field.inputType === 'date' || role === 'shipDate') {
        return String(raw).slice(0, 10);
    }
    return String(raw);
};

const buildSequentialDetailExportRows = (rows, config, listSummary, managementScope, fixedColumns) => {
    const list = Array.isArray(rows) ? rows : [];
    const formConfig = normalizeAsRepairFormConfig(config);
    const fieldMap = getAsRepairExcelFieldMap(formConfig, managementScope);
    const fixedFieldKeys = new Set(
        fixedColumns
            .map((col) => fieldMap[col.role]?.key)
            .filter(Boolean),
    );
    const detailFields = getAsRepairDetailExportFields(formConfig, listSummary, fixedFieldKeys);
    const headers = [
        ...fixedColumns.map((col) => col.header),
        ...detailFields.map((field) => field.label || field.key),
    ];

    const bodyRows = list.map((row) => {
        const fixedCells = fixedColumns.map((col) => (
            cellValueForExport(fieldMap[col.role], row, formConfig, col.role)
        ));
        const detailCells = detailFields.map((field) => (
            cellValueForExport(field, row, formConfig)
        ));
        return [...fixedCells, ...detailCells];
    });

    return {
        rows: [headers, ...bodyRows],
        rowCount: bodyRows.length,
    };
};

/** A/S 수리 관리 — 수리내역 리스트 (1행 헤더 + A열부터 연속) */
export const buildAsRepairRepairHistoryExcelRows = (rows, config, listSummary) => (
    buildSequentialDetailExportRows(
        rows,
        config,
        listSummary,
        AS_REPAIR_MANAGEMENT_SCOPE.REPAIR,
        REPAIR_HISTORY_FIXED_COLUMNS,
    )
);

/** A/S 개인 관리 — 수리완료 리스트 (1행 헤더 + A열부터 연속) */
export const buildAsRepairPersonalRepairCompleteExcelRows = (rows, config, listSummary) => (
    buildSequentialDetailExportRows(
        rows,
        config,
        listSummary,
        AS_REPAIR_MANAGEMENT_SCOPE.PERSONAL,
        PERSONAL_REPAIR_COMPLETE_FIXED_COLUMNS,
    )
);

const buildExportFileName = (managementScope, { detailListExport = false } = {}) => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}`;
    let prefix = 'AS수리목록';
    if (managementScope === AS_REPAIR_MANAGEMENT_SCOPE.PERSONAL) {
        prefix = detailListExport ? 'AS수리완료목록' : 'AS개인목록';
    } else if (detailListExport) {
        prefix = 'AS수리내역목록';
    }
    return `${prefix}_${stamp}.xlsx`;
};

/** 선택한 A/S 목록을 XLSX로 저장 */
export const downloadAsRepairExcelFile = (
    rows,
    config,
    { fileName, managementScope, listSummary, detailListExport = false } = {},
) => {
    const scope = managementScope || AS_REPAIR_MANAGEMENT_SCOPE.REPAIR;
    const list = Array.isArray(rows) ? rows : [];
    if (list.length === 0) {
        return { ok: false, reason: 'empty' };
    }

    let dataRows = [];
    let rowCount = 0;

    if (detailListExport && scope === AS_REPAIR_MANAGEMENT_SCOPE.PERSONAL) {
        const built = buildAsRepairPersonalRepairCompleteExcelRows(list, config, listSummary);
        dataRows = built.rows;
        rowCount = built.rowCount;
    } else if (detailListExport && scope === AS_REPAIR_MANAGEMENT_SCOPE.REPAIR) {
        const built = buildAsRepairRepairHistoryExcelRows(list, config, listSummary);
        dataRows = built.rows;
        rowCount = built.rowCount;
    } else {
        return { ok: false, reason: 'empty' };
    }

    if (dataRows.length === 0) {
        return { ok: false, reason: 'empty' };
    }

    const sheet = XLSX.utils.aoa_to_sheet(dataRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, 'Sheet1');
    XLSX.writeFile(
        workbook,
        fileName || buildExportFileName(scope, { detailListExport }),
    );
    return { ok: true, rowCount };
};
